import { Controller } from 'react-hook-form';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { clsx } from 'clsx';
import Checkbox from 'components/Checkbox/Checkbox';
import DataSetsItemButtons from 'components/DataSetsItemButtons/DataSetsItemButtons';
import TableCell from 'components/Table/TableCell';
import TableRow from 'components/Table/TableRow';
import { MILLION, NACE_CODE_OPTIONS, NACE_SECTION_OPTIONS } from 'constants/dataSets';
import { SELECTED_ITEMS } from 'constants/formFields';
import formattedData from 'utils/formattedData';
import formattedNumberWithComma from 'utils/formattedNumberWithComma';
import getLabels from 'utils/getLabels';

import type { IProfitCentresItem } from './interfaces/IProfitCentresItem';

import styles from './scss/ProfitCentresItem.module.scss';

function ProfitCentresItem({
  item, control, onArchiveModalInfoOpen, onMoveToArchive, onDeArchive, onEditRecord, isActionsVisible,
  locationsCountries, onDeleteRecord,
}: IProfitCentresItem): React.ReactElement {
  const {
    id, name, uniqueIdentifier, valueChainSection, ownership,
    equityShareholding, financialControl, numberOfEmployees, dateOpened, dateClosed,
    archived, latestArchivation, naceSection, naceCode, euTaxonomyCode, startDate, endDate,
    addressCountry, addressState, addressPostCode, addressCity, addressStreet, addressBuilding,
    addressCoordinates, netBookValue, totalRevenueRolling12MActual, totalGrossMarginRolling12MActual,
    wipFinishedGoodsStock, virtualProperties,
  } = item;

  const locationCountry = (countryCode: string) => {
    const country = locationsCountries?.find((countryItem) => countryItem.iso2 === countryCode);
    return country?.name || countryCode;
  };

  return (
    <TableRow className={clsx(undefined, { [styles.archived]: archived })}>
      <TableCell>
        <div className={styles.staticCell}>
          {archived ? (
            <span>
              {name || '_'}
              {latestArchivation ? (
                <InfoIcon
                  onClick={() => onArchiveModalInfoOpen(latestArchivation)}
                />
              ) : null}
            </span>
          ) : null}

          {!archived && (
            isActionsVisible ? (
              <Controller
                control={control}
                render={({ field: { onChange, value, disabled } }) => (
                  <Checkbox
                    key={id}
                    name={`${SELECTED_ITEMS}-${id}`}
                    checked={!!value.includes(id)}
                    value={id}
                    disabled={disabled}
                    onChange={(event) => {
                      onChange(event.target.checked
                        ? [...value, id]
                        : value.filter((selectedItem) => selectedItem !== id));
                    }}
                    label={(<span>{name}</span>)}
                  />
                )}
                name={SELECTED_ITEMS}
              />
            ) : (<span>{name}</span>)
          )}
        </div>
      </TableCell>
      <TableCell>
        {uniqueIdentifier || '-'}
      </TableCell>
      <TableCell>
        {valueChainSection || '-'}
      </TableCell>
      <TableCell>
        {ownership || '-'}
      </TableCell>
      <TableCell>
        { equityShareholding ? `${formattedNumberWithComma(equityShareholding)} %` : '_'}
      </TableCell>
      <TableCell>
        { financialControl ? `${formattedNumberWithComma(financialControl)} %` : '_'}
      </TableCell>
      <TableCell>
        {formattedNumberWithComma(numberOfEmployees) || '_'}
      </TableCell>
      <TableCell>
        {dateOpened ? formattedData(dateOpened) : '_'}
      </TableCell>
      <TableCell>
        {dateClosed ? formattedData(dateClosed) : '_'}
      </TableCell>
      <TableCell>
        {getLabels(naceSection, NACE_SECTION_OPTIONS) || '_'}
      </TableCell>
      <TableCell>
        {getLabels(naceCode, NACE_CODE_OPTIONS) || '_'}
      </TableCell>
      <TableCell>
        {euTaxonomyCode || '_'}
      </TableCell>
      <TableCell>
        {startDate ? formattedData(startDate) : '_'}
      </TableCell>
      <TableCell>
        {endDate ? formattedData(endDate) : '_'}
      </TableCell>
      <TableCell>
        {netBookValue?.amount ? `${formattedNumberWithComma(netBookValue.amount / MILLION)} M ${netBookValue?.currency}`
          : '_'}
      </TableCell>
      <TableCell>
        {totalRevenueRolling12MActual?.amount
          ? `${formattedNumberWithComma(totalRevenueRolling12MActual.amount / MILLION)} `
          + `M ${totalRevenueRolling12MActual?.currency}`
          : '_'}
      </TableCell>
      <TableCell>
        { virtualProperties?.totalRevenuePercents
          ? `${formattedNumberWithComma(virtualProperties?.totalRevenuePercents)} %` : '_'}
      </TableCell>
      <TableCell>
        {totalGrossMarginRolling12MActual?.amount
          ? `${formattedNumberWithComma(totalGrossMarginRolling12MActual.amount / MILLION)} `
          + `M ${totalGrossMarginRolling12MActual?.currency}`
          : '_'}
      </TableCell>
      <TableCell>
        { virtualProperties?.totalGrossProfitPercents
          ? `${formattedNumberWithComma(virtualProperties?.totalGrossProfitPercents)} %` : '_'}
      </TableCell>
      <TableCell>
        {wipFinishedGoodsStock?.amount
          ? `${formattedNumberWithComma(wipFinishedGoodsStock.amount / MILLION)} `
          + `M ${wipFinishedGoodsStock?.currency}`
          : '_'}
      </TableCell>
      <TableCell>
        {locationCountry(addressCountry) || '_'}
      </TableCell>
      <TableCell>
        {addressState || '_'}
      </TableCell>
      <TableCell>
        {addressCity || '_'}
      </TableCell>
      <TableCell>
        {addressPostCode || '_'}
      </TableCell>
      <TableCell>
        {addressStreet || '_'}
      </TableCell>
      <TableCell>
        {addressBuilding || '_'}
      </TableCell>
      <TableCell>
        {addressCoordinates || '_'}
      </TableCell>
      {isActionsVisible ? (
        <TableCell>
          <DataSetsItemButtons
            id={id}
            archived={archived}
            onEditRecord={onEditRecord}
            onMoveToArchive={onMoveToArchive}
            onDeArchive={onDeArchive}
            onDeleteRecord={onDeleteRecord}
          />
        </TableCell>
      ) : null}
    </TableRow>
  );
}

export default ProfitCentresItem;
